<template>
  <div id="vendasOnlineLista">
    <q-banner v-if="avisos.length" dense class="bg-blue-1 text-blue-10 q-mb-md" rounded data-test="avisos-sem-venda">
      <template #avatar>
        <q-icon name="info" color="primary" />
      </template>
      <q-list dense>
        <q-item v-for="aviso in avisos" :key="aviso.id" class="q-px-none" :data-test="`aviso-${aviso.id}`">
          <q-item-section>
            <q-item-label class="text-weight-bold">{{ formatConflitoMotivo(aviso.motivo) }}</q-item-label>
            <q-item-label caption>
              {{ formatMomento(aviso.created) }}<span v-if="aviso.payload && aviso.payload.id_transaction"> · transação {{ aviso.payload.id_transaction }}</span>
            </q-item-label>
          </q-item-section>
          <q-item-section v-if="podeResolver" side>
            <q-btn flat dense no-caps color="primary" label="Resolver" :data-test="`resolver-aviso-${aviso.id}`" @click="$emit('resolver-aviso', aviso)" />
          </q-item-section>
        </q-item>
      </q-list>
    </q-banner>

    <q-table
      dense
      :rows="vendas"
      :columns="columns"
      row-key="id"
      :loading="carregando"
      v-model:pagination="paginacao"
      :rows-per-page-options="[25, 50, 100]"
      :visible-columns="visibleColumns"
      binary-state-sort
      no-data-label="Nenhuma venda online encontrada"
      no-results-label="Nenhum resultado obtido"
      loading-label="Carregando dados"
      @request="requestPagina"
    >
      <template v-slot:top="props">
        <div class="q-table__title">Vendas Online</div>
        <q-space />

        <vendas-online-periodo :model-value="periodo" class="q-mr-sm" @update:model-value="(valor) => $emit('update:periodo', valor)" />

        <q-btn flat dense no-wrap v-ripple color="primary" no-caps :label="rotuloStatus" class="q-ml-sm q-px-md bg-white text-blue-7" style="margin-right: 10px" data-test="filtro-status">
          <q-menu anchor="top right" self="top right">
            <q-list class="text-grey-8" style="min-width: 100px">
              <q-item aria-hidden="true">
                <q-item-section class="text-uppercase text-grey-7" style="font-size: 0.7rem">Status</q-item-section>
              </q-item>
              <q-item :class="{ active_status: !status }" clickable v-close-popup aria-hidden="true" data-test="status-item-todos" @click="$emit('update:status', '')">
                <q-item-section>Todos</q-item-section>
              </q-item>
              <q-item
                v-for="chave in ordemStatus"
                :key="chave"
                :class="{ active_status: status === chave }"
                clickable
                v-close-popup
                aria-hidden="true"
                :data-test="`status-item-${chave}`"
                @click="$emit('update:status', chave)"
              >
                <q-item-section>{{ rotulosStatus[chave].rotulo }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>

        <q-btn flat dense no-wrap v-ripple color="primary" no-caps :label="rotuloOperadora" class="q-ml-sm q-px-md bg-white text-blue-7" style="margin-right: 10px" data-test="filtro-operadora">
          <q-menu anchor="top right" self="top right">
            <q-list class="text-grey-8" style="min-width: 100px">
              <q-item aria-hidden="true">
                <q-item-section class="text-uppercase text-grey-7" style="font-size: 0.7rem">Operador</q-item-section>
              </q-item>
              <q-item :class="{ active_status: !operadoraId }" clickable v-close-popup aria-hidden="true" data-test="operadora-item-todas" @click="$emit('update:operadoraId', null)">
                <q-item-section>Todos</q-item-section>
              </q-item>
              <q-item
                v-for="operadora in operadoras"
                :key="operadora.value"
                :class="{ active_status: operadoraId === operadora.value }"
                clickable
                v-close-popup
                aria-hidden="true"
                :data-test="`operadora-item-${operadora.value}`"
                @click="$emit('update:operadoraId', operadora.value)"
              >
                <q-item-section>{{ operadora.label }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>

        <q-select
          v-model="visibleColumns"
          multiple
          borderless
          dense
          options-dense
          display-value="Colunas"
          emit-value
          map-options
          :options="colunasOpcionais"
          option-value="name"
          style="min-width: 100px"
        />

        <q-btn flat dense rounded no-wrap v-ripple class="q-ml-sm q-px-md bg-white text-black" no-caps icon="more_vert" style="margin-right: 10px" data-test="mais-opcoes">
          <q-menu anchor="top right" self="top right">
            <q-list class="text-grey-8" style="min-width: 100px">
              <q-item aria-hidden="true">
                <q-item-section class="text-uppercase text-grey-7" style="font-size: 0.7rem">Mais Opções</q-item-section>
              </q-item>
              <q-item clickable v-close-popup aria-hidden="true" data-test="atualizar" @click="$emit('atualizar')">
                <q-item-section>Atualizar</q-item-section>
              </q-item>
              <q-item clickable v-close-popup aria-hidden="true" @click="props.toggleFullscreen">
                <q-item-section>{{ props.inFullscreen ? "Minimizar" : "Maximizar Tela" }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props" class="cursor-pointer" :data-test="`venda-${props.row.codigo}`" @click="$emit('abrir', props.row)">
          <q-td key="selecao" auto-width @click.stop>
            <q-checkbox
              v-if="isSelecionavel(props.row)"
              dense
              color="secondary"
              :model-value="selecionadas.includes(props.row.id)"
              :data-test="`selecionar-${props.row.codigo}`"
              @update:model-value="toggleSelecao(props.row)"
            />
          </q-td>
          <q-td key="status" :props="props">
            <q-chip dense square text-color="white" :color="statusDe(props.row).cor" :icon="statusDe(props.row).icone" :data-test="`selo-${props.row.status_lista}`">
              <q-spinner v-if="props.row.status === 'registrando'" size="1em" class="q-mr-xs" data-test="registrando-indicador" />
              {{ statusDe(props.row).rotulo }}
            </q-chip>
            <contagem-cancelamento v-if="props.row.status === 'cancelando'" :prazo="props.row.cancelamento_prazo_em" />
            <div v-if="formatRegistroTentativas(props.row)" class="text-caption text-deep-orange-8" data-test="tentativas-registro">
              {{ formatRegistroTentativas(props.row) }}
            </div>
            <template v-if="props.row.status_lista === 'conflito'">
              <div v-for="motivo in props.row.motivos || []" :key="motivo" class="text-caption text-red-9">{{ formatConflitoMotivo(motivo) }}</div>
              <div v-if="(props.row.aguardando || []).length" class="text-caption text-orange-10" :data-test="`aguardando-${props.row.codigo}`">
                Aguardando a causa: {{ props.row.aguardando.map(formatConflitoMotivo).join(", ") }}
              </div>
            </template>
          </q-td>
          <q-td key="codigo" :props="props">{{ props.row.codigo }}</q-td>
          <q-td key="created" :props="props">{{ formatMomento(props.row.created) }}</q-td>
          <q-td key="cliente_nome" :props="props">{{ props.row.cliente_nome }}</q-td>
          <q-td key="operadora_nome" :props="props">{{ props.row.operadora_nome }}</q-td>
          <q-td key="valor_total" :props="props">R$ {{ $currency(props.row.valor_total) }}</q-td>
          <q-td key="corte_em" :props="props">{{ formatMomento(props.row.corte_em) }}</q-td>
          <q-td key="pago_em" :props="props">{{ formatMomento(props.row.pago_em) }}</q-td>
          <q-td key="acoes" auto-width class="text-right" @click.stop>
            <q-btn flat round dense size="sm" color="primary" icon="visibility" :data-test="`detalhe-${props.row.codigo}`" @click="$emit('abrir', props.row)">
              <q-tooltip>Detalhe</q-tooltip>
            </q-btn>
            <q-btn v-if="temLink(props.row)" flat round dense size="sm" color="primary" icon="content_copy" :data-test="`copiar-link-${props.row.codigo}`" @click="$emit('copiar', props.row)">
              <q-tooltip>Copiar link</q-tooltip>
            </q-btn>
            <q-btn v-if="temLink(props.row) && podeCriar" flat round dense size="sm" color="primary" icon="send" :data-test="`reenviar-link-${props.row.codigo}`" @click="$emit('reenviar', props.row)">
              <q-tooltip>Reenviar link</q-tooltip>
            </q-btn>
            <q-btn v-if="isSelecionavel(props.row)" flat round dense size="sm" color="negative" icon="block" :data-test="`cancelar-venda-${props.row.codigo}`" @click="$emit('cancelar', props.row)">
              <q-tooltip>Cancelar venda</q-tooltip>
            </q-btn>
            <q-btn v-if="isResolvivel(props.row)" flat dense no-caps size="sm" color="primary" label="Resolver" :data-test="`resolver-${props.row.codigo}`" @click="$emit('resolver', props.row)" />
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import ContagemCancelamento from "@/components/VendasOnline/contagem-cancelamento.vue";
import VendasOnlinePeriodo from "@/components/VendasOnline/vendas-online-periodo.vue";
import {
  VENDA_ONLINE_CANCELAVEIS,
  VENDA_ONLINE_LISTA_STATUS,
  formatConflitoMotivo,
  formatMomento,
  formatRegistroTentativas,
  getVendaOnlineListaStatus
} from "@/utils/venda-online";
import { VENDA_ONLINE_LISTA_ORDEM } from "@/utils/venda-online-painel";

const COLUNAS = [
  { name: "selecao", label: "", field: "id", required: true },
  { name: "status", label: "Status", field: "status_lista", align: "left", sortable: true },
  { name: "codigo", label: "Código", field: "codigo", align: "left", sortable: true },
  { name: "created", label: "Criada em", field: "created", align: "left", sortable: true },
  { name: "cliente_nome", label: "Cliente", field: "cliente_nome", align: "left", sortable: true },
  { name: "operadora_nome", label: "Operador", field: "operadora_nome", align: "left", sortable: true },
  { name: "valor_total", label: "Valor", field: "valor_total", align: "right", sortable: true },
  { name: "corte_em", label: "Vale até", field: "corte_em", align: "left", sortable: true },
  { name: "pago_em", label: "Pago em", field: "pago_em", align: "left", sortable: true },
  { name: "acoes", label: "Ações", field: "id", align: "right", required: true }
];

const COLUNAS_PADRAO = ["status", "codigo", "created", "cliente_nome", "operadora_nome", "valor_total", "corte_em"];

export default {
  name: "vendas-online-lista",
  components: { ContagemCancelamento, VendasOnlinePeriodo },
  props: {
    vendas: { type: Array, default: () => [] },
    avisos: { type: Array, default: () => [] },
    paging: { type: Object, required: true },
    carregando: { type: Boolean, default: false },
    status: { type: String, default: "" },
    periodo: { type: Object, required: true },
    operadoraId: { type: Number, default: null },
    operadoras: { type: Array, default: () => [] },
    selecionadas: { type: Array, default: () => [] },
    podeCriar: { type: Boolean, default: false },
    podeResolver: { type: Boolean, default: false }
  },
  emits: ["abrir", "pedir", "copiar", "reenviar", "cancelar", "resolver", "resolver-aviso", "atualizar", "update:status", "update:periodo", "update:operadoraId", "update:selecionadas"],
  data() {
    return {
      columns: COLUNAS,
      visibleColumns: [...COLUNAS_PADRAO],
      paginacao: { page: 1, rowsPerPage: 25, rowsNumber: 0, sortBy: "created", descending: true },
      ordemStatus: VENDA_ONLINE_LISTA_ORDEM,
      rotulosStatus: VENDA_ONLINE_LISTA_STATUS
    };
  },
  computed: {
    colunasOpcionais() {
      return this.columns.filter((coluna) => !coluna.required);
    },
    rotuloStatus() {
      return this.status ? getVendaOnlineListaStatus(this.status).rotulo : "Status";
    },
    rotuloOperadora() {
      const escolhida = this.operadoras.find((operadora) => operadora.value === this.operadoraId);

      return escolhida ? escolhida.label : "Operador";
    }
  },
  watch: {
    paging: {
      immediate: true,
      handler(paging) {
        this.paginacao = {
          ...this.paginacao,
          page: paging.page || 1,
          rowsPerPage: paging.limit || 25,
          rowsNumber: paging.count || 0
        };
      }
    }
  },
  methods: {
    formatConflitoMotivo,
    formatMomento,
    formatRegistroTentativas,
    statusDe(venda) {
      return getVendaOnlineListaStatus(venda.status_lista);
    },
    temLink(venda) {
      return venda.status === "ativa";
    },
    isSelecionavel(venda) {
      return this.podeCriar && VENDA_ONLINE_CANCELAVEIS.includes(venda.status);
    },
    isResolvivel(venda) {
      return this.podeResolver && venda.status_lista === "conflito" && (venda.motivos || []).length > 0;
    },
    toggleSelecao(venda) {
      this.$emit(
        "update:selecionadas",
        this.selecionadas.includes(venda.id) ? this.selecionadas.filter((id) => id !== venda.id) : [...this.selecionadas, venda.id]
      );
    },
    requestPagina({ pagination }) {
      this.paginacao = { ...this.paginacao, sortBy: pagination.sortBy, descending: pagination.descending };
      this.$emit("pedir", {
        page: pagination.page,
        limit: pagination.rowsPerPage,
        sort: pagination.sortBy,
        direction: pagination.descending ? "desc" : "asc"
      });
    }
  }
};
</script>
